import React, { useState } from 'react'
import {
  CBadge,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilOptions, cilPencil, cilTrash } from '@coreui/icons'
import moment from 'moment'
import { toast } from 'react-toastify'
import ToastComponent from 'src/components/common/TaostComponent.js'
import AdsManagerService from 'src/service/AdsManagerService'
import Notify from '../Notify'
import { PLACEMENT_OPTIONS } from './promoFormConfig'

const formatDate = (value) => (value ? moment(value).format('D.MM.YYYY') : '—')

const placementLabel = (value) =>
  PLACEMENT_OPTIONS.find((option) => option.value === value)?.text || value

const AdminPromoTable = ({
  users,
  fetchUsers,
  setActivePage,
  setItemsPerPage,
  setColumnFilter,
  setColumnSorter,
  itemsPerPage,
  activePage,
  columnSorter,
  loading,
  onEditPromo,
}) => {
  const API_URL = process.env.REACT_APP_API_URL

  const columns = [
    {
      label: 'Logo',
      key: 'logo',
      _style: { width: '8%' },
      filter: false,
      sorter: false,
    },
    { label: 'Title', key: 'title' },
    { label: 'Label', key: 'promo_label', filter: false },
    { label: 'Placement', key: 'placement', filter: false, sorter: false },
    { label: 'Validity', key: 'validity_date', filter: false },
    { label: 'Start Date', key: 'schedule_start_date', filter: false },
    { label: 'End Date', key: 'schedule_end_date', filter: false },
    {
      key: 'actions',
      label: 'Actions',
      _style: { width: '5%' },
      filter: false,
      sorter: false,
    },
  ]

  // Are you sure want modal
  const [handleYes, setHandleYes] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [handleNo, setHandleNo] = useState(false)
  const [tableId, setTableId] = useState(false)

  const handleCancel = () => setShowConfirm(false)

  const handleConfirm = () => {
    deleteAds(tableId)
    return setShowConfirm(false)
  }

  const areYouSureModal = (id) => {
    setShowConfirm(true)
    setTableId(id)
  }

  const deleteAds = (id) => {
    AdsManagerService.deleteAds({ id }).then((res) => {
      if (res.status === 200) {
        toast.dismiss()
        fetchUsers()
        ToastComponent(res.message, 'success')
      } else {
        ToastComponent(res.message, 'error')
      }
    })
  }

  const totalPages = Math.ceil((users?.total || 0) / itemsPerPage)
  const showPagination = totalPages > 1

  return (
    <div className="ge-datatable ge-datatable--static">
      <CSmartTable
        columns={columns}
        columnFilter={{
          external: true,
        }}
        columnSorter={{
          external: true,
        }}
        sorterValue={columnSorter}
        scopedColumns={{
          logo: (item) => (
            <td>
              {item.logo ? (
                <img
                  src={`${API_URL}uploads/ads/${item.logo}`}
                  alt="promo"
                  width="40"
                  height="40"
                  className="rounded"
                />
              ) : (
                <span className="text-medium-emphasis">—</span>
              )}
            </td>
          ),
          title: (item) => (
            <td>
              <span
                className="fw-semibold text-primary"
                role="button"
                onClick={() => onEditPromo(item.id)}
              >
                {item.title}
              </span>
            </td>
          ),
          promo_label: (item) => (
            <td>{item.promo_label || <span className="text-medium-emphasis">—</span>}</td>
          ),
          placement: (item) => {
            const placements = Array.isArray(item.placement) ? item.placement : []
            return (
              <td>
                {placements.length ? (
                  <div className="d-flex flex-wrap gap-1">
                    {placements.map((value) => (
                      <CBadge color="primary" shape="rounded-pill" key={value}>
                        {placementLabel(value)}
                      </CBadge>
                    ))}
                  </div>
                ) : (
                  <span className="text-medium-emphasis">—</span>
                )}
              </td>
            )
          },
          validity_date: (item) => <td>{formatDate(item.validity_date)}</td>,
          schedule_start_date: (item) => <td>{formatDate(item.schedule_start_date)}</td>,
          schedule_end_date: (item) => <td>{formatDate(item.schedule_end_date)}</td>,
          actions: (item) => (
            <td className="py-2">
              <CDropdown alignment="end" portal>
                <CDropdownToggle color="transparent" caret={false} className="p-0">
                  <CIcon icon={cilOptions} role="button" />
                </CDropdownToggle>
                <CDropdownMenu>
                  <CDropdownItem role="button" onClick={() => onEditPromo(item.id)}>
                    <CIcon icon={cilPencil} className="me-2" />
                    Edit
                  </CDropdownItem>
                  <CDropdownItem
                    role="button"
                    className="text-danger"
                    onClick={() => areYouSureModal(item.id)}
                  >
                    <CIcon icon={cilTrash} className="me-2" />
                    Delete
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </td>
          ),
        }}
        items={users?.data}
        itemsPerPage={itemsPerPage}
        itemsPerPageSelect
        loading={loading}
        pagination={showPagination ? { external: true } : false}
        paginationProps={
          showPagination
            ? {
                activePage: activePage,
                pages: totalPages,
              }
            : undefined
        }
        tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
        onActivePageChange={(activePage) => setActivePage(activePage)}
        onColumnFilterChange={(filter) => {
          setActivePage(1)
          setColumnFilter(filter)
        }}
        onItemsPerPageChange={(itemsPerPage) => {
          setActivePage(1)
          setItemsPerPage(itemsPerPage)
        }}
        onSorterChange={(sorter) => setColumnSorter(sorter)}
      />
      <Notify
        setShowConfirm={setShowConfirm}
        showConfirm={showConfirm}
        setHandleNo={setHandleNo}
        handleNo={handleNo}
        handleYes={handleYes}
        setHandleYes={setHandleYes}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        text="Are you sure you want to delete this promo card?"
      />
    </div>
  )
}

export default AdminPromoTable
